import React, { useState } from "react";
import { usePost } from "redux/fetch-hooks";

import { CardLevelOne } from "common/DS/ElevatedCard";
import { Subtitle2 } from "common/DS/Text";
import TextField from "common/DS/TextField";
import QuestionButtonGroup from "common/Question/QuestionButtonGroup";
import { heapTrack } from "lib/analytics";
import { Checkbox } from "common/DS/Checkables";

const HubAuthCard = (
    {
        title = "Welcome to WorkStep Surveys!",
        subtitle = "To begin your survey, please log in.",
        labelText = "Email or Phone Number",
        placeholder = "Email or Phone Number",
        hasError,
        setHasError,
        input,
        setInput,
        onNext,
        onBack,
        onResend,
        helperText,
        buttonRenamer,
        nextDisabled,
        rememberMe,
        setRememberMe,
    }: {
        title?: string;
        subtitle?: string;
        labelText?: string;
        placeholder?: string;
        hasError: boolean;
        setHasError: (hasError: boolean) => void;
        input: string;
        setInput: (input: string) => void;
        onNext: () => void;
        onBack?: () => void;
        onResend?: () => void;
        helperText?: string;
        buttonRenamer?: object;
        nextDisabled?: boolean;
        rememberMe?: boolean;
        setRememberMe?: (rememberMe: boolean) => void;
    },
    { t: _ }: { t: (key: string, variables?: object) => string },
) => {
    const [hasInput, setHasInput] = useState(false);
    const buttonClassName = "hub__login-buttons";

    function onTextChange(textInput: string) {
        const textEntered = textInput.trim();
        setHasInput(textEntered.length > 0);
        setInput(textEntered);
        setHasError(false);
    }

    return (
        <CardLevelOne className="hub__login">
            <div className="hub__login-title">{title}</div>
            <Subtitle2 className="hub__login-subtitle">{subtitle}</Subtitle2>
            <TextField
                className="hub__login-text-box"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={input}
                placeholder={placeholder}
                onChange={(e) => onTextChange(e.target.value)}
                label={input ? labelText : ""}
                helperText={helperText || ""}
                error={hasError}
            />
            {setRememberMe && (
                <Checkbox checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}>
                    <span>Remember me</span>
                </Checkbox>
            )}
            <QuestionButtonGroup
                handleSubmit={onNext}
                handleBack={onBack}
                handleSkip={onResend}
                nextDisabled={!hasInput || nextDisabled}
                additionalClassNames={buttonClassName}
                buttonRenamer={buttonRenamer}
            />
        </CardLevelOne>
    );
};

export const CodeRequestCard = (
    {
        setVisible,
        setContactInfo,
        showDisplayCode,
    }: {
        setVisible: (visible: boolean) => void;
        setContactInfo: (contactInfo: string) => void;
        showDisplayCode: (visible: boolean) => void;
    },
    { t: _ }: { t: (key: string, variables?: object) => string },
) => {
    const [hasError, setHasError] = useState(false);
    const [input, setInput] = useState("");
    const [helperText, setHelperText] = useState("");
    const accountFormatErrorText = "Please enter a valid email or phone number";
    const [postAuthCode, { isLoading }] = usePost("/check-in/auth/code", {
        onSuccess: () => {
            setHelperText("");
            setVisible(false);
            showDisplayCode(true);
        },
        onError: (error) => {
            setHelperText(error.message);
            setHasError(true);
        },
    });

    function validate() {
        if (!input.length) {
            setHelperText(accountFormatErrorText);
            setHasError(true);
            return false;
        }

        return true;
    }
    function onNext() {
        setHasError(false);
        const canSubmit = validate();
        if (canSubmit) {
            setContactInfo(input);
            postAuthCode({
                data: {
                    csrf_token: window.csrfToken,
                    handle: input,
                    code_type: input.includes("@") ? "email" : "mobile",
                },
            });
        }
    }

    return (
        <HubAuthCard
            labelText="Email or Phone Number"
            placeholder="Email or Phone Number"
            hasError={hasError}
            setHasError={setHasError}
            input={input}
            setInput={setInput}
            onNext={onNext}
            helperText={helperText}
            nextDisabled={isLoading}
        />
    );
};

export const EnterCodeCard = (
    {
        contactInfo,
        setVisible,
        setLoginVisible,
        setAuthenticated,
    }: {
        contactInfo: string;
        setVisible: (visible: boolean) => void;
        setLoginVisible: (visible: boolean) => void;
        setAuthenticated: (authenticated: boolean) => void;
    },
    { t: _ }: { t: (key: string, variables?: object) => string },
) => {
    const [hasError, setHasError] = useState(false);
    const [input, setInput] = useState("");
    const [helperText, setHelperText] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const enterCodeSubtitle = `We sent a code to ${contactInfo}`;
    const buttonRenamer = { skip: "Resend Code", next: "Log In" };
    const [postAuthCodeVerify, { isLoading }] = usePost("/check-in/auth/code/verify", {
        onSuccess: () => {
            heapTrack("Hub 2.0 Login - redirecting to survey");
            setLoginVisible(false);
            setVisible(false);
            setAuthenticated(true);
        },
        onError: (error) => {
            setHelperText(error.message);
            setHasError(true);
        },
    });

    function onNext() {
        postAuthCodeVerify({
            data: {
                csrf_token: window.csrfToken,
                handle: contactInfo,
                code: input,
                code_type: contactInfo.includes("@") ? "email" : "mobile",
                remember_me: rememberMe,
            },
        });
    }

    function onResend() {
        setInput("");
        setHasError(false);
        setHelperText("");
    }

    function onBack() {
        setHasError(false);
        setInput("");
        setHelperText("");
        setVisible(false);
        setLoginVisible(true);
        setRememberMe(false);
    }

    return (
        <HubAuthCard
            title="Enter Code"
            subtitle={enterCodeSubtitle}
            labelText="Enter 6 digit code"
            placeholder="Enter 6 digit code"
            helperText={helperText}
            hasError={hasError}
            setHasError={setHasError}
            input={input}
            setInput={setInput}
            onNext={onNext}
            onResend={onResend}
            onBack={onBack}
            buttonRenamer={buttonRenamer}
            nextDisabled={isLoading}
            rememberMe={rememberMe}
            setRememberMe={setRememberMe}
        />
    );
};
